import AddPhotoIcon from "@mui/icons-material/AddAPhoto";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import clsx from "clsx";
import FilterHdrIcon from "@mui/icons-material/FilterHdr";
import { styled, SxProps, Theme, useTheme } from "@mui/material/styles";
import {
	convertImageLabelToNumberedKey,
	DEFAULT_IMAGE_LABEL,
	getImageAttributeBySize,
	getImageContainerNumberedKeys,
	getImageSizeByDimensions,
	getImageSrc,
	IMAGE_SIZE_ICON,
	IMAGE_SIZE_LARGE,
	IMAGE_SIZE_LARGEST,
	IMAGE_SIZE_MEDIUM,
	IMAGE_SIZE_SMALL,
	IMAGE_SIZE_SMALLEST,
	ImageDimensions
} from "csi/model/image";
import { C8Node } from "csi/typings";
import { useResizeObserver, useSyncLocalStore } from "designSystem/hooks";
import { boxShadows, grey } from "designSystem/styles/color";
import { cursorPointer, imageLarge, imageLargest, imageMedium, imageSmall, imageSmallest } from "designSystem/styles/css";
import { debounce } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import { observer } from "mobx-react-lite";
import React, { CSSProperties, Fragment, FunctionComponent, ReactNode, useEffect } from "react";
import Box, { BoxProps } from "../box/Box";
import IconButton from "../button/IconButton";
import Tooltip from "../tooltip/Tooltip";

// TODO: handle sessionStore.printViewableImage

const CSS_MAP = {
	smallest: imageSmallest,
	small: imageSmall,
	medium: imageMedium,
	large: imageLarge,
	largest: imageLargest,
};

const SIZE_TO_ENUM_MAP = {
	default: IMAGE_SIZE_SMALL,
	icon: IMAGE_SIZE_ICON,
	smallest: IMAGE_SIZE_SMALLEST,
	small: IMAGE_SIZE_SMALL,
	medium: IMAGE_SIZE_MEDIUM,
	large: IMAGE_SIZE_LARGE,
	largest: IMAGE_SIZE_LARGEST,
};

const ENUM_TO_SIZE_MAP = {
	[IMAGE_SIZE_ICON]: "icon",
	[IMAGE_SIZE_SMALLEST]: "smallest",
	[IMAGE_SIZE_SMALL]: "small",
	[IMAGE_SIZE_MEDIUM]: "medium",
	[IMAGE_SIZE_LARGE]: "large",
	[IMAGE_SIZE_LARGEST]: "largest",
};

const PREFIX = "CsiImage";

const classes = {
	arrow: `${PREFIX}arrow`,
	arrowLeft: `${PREFIX}arrowLeft`,
	arrowRight: `${PREFIX}arrowRight`,
	fullSize: `${PREFIX}fullSize`,
	image: `${PREFIX}image`,
	pointerEventsAuto: `${PREFIX}pointerEventsAuto`,
	max: `${PREFIX}max`,
};

const StyledBox = styled(Box)(({
	theme,
}) => ({
	position: "relative",
	textAlign: "center",

	[`& .${classes.arrow}`]: {
		boxShadow: boxShadows.popper,
		position: "absolute",
	},
	[`& .${classes.arrowLeft}`]: {
		left: theme.spacing(3),
	},
	[`& .${classes.arrowRight}`]: {
		right: theme.spacing(3),
	},
	[`&.${classes.fullSize}`]: {
		height: "100%",
		width: "100%",
	},
	[`& .${classes.image}`]: {
		userSelect: "none",
		verticalAlign: "middle",
	},
	[`& .${classes.pointerEventsAuto}`]: {
		pointerEvents: "auto",
	},
	[`& .${classes.max}`]: {
		maxHeight: "100%",
		maxWidth: "100%",
	},
}));

const toolTipStyles = (theme: Theme): SxProps => {
	return {
		backgroundColor: theme.palette.background.paper,
		boxShadow: boxShadows.dialog,
		padding: theme.spacing(1),
	};
};

export interface CsiImageProps extends Omit<BoxProps, "children"> {
	/** indicates if can use arrows to switch to other node images if available (this will not work if path is specified or rendering icon) */
	allowSwitchNodeImages?: boolean;

	/** indicates if viewable image should be used if requested size requires it */
	allowViewable?: boolean;

	/** aspect ratio of image container that will affect calculation of height based on container width (width / aspectRatio) */
	aspectRatio?: number;

	/** border radius for the image to round the image - any actual border around the image should be applied on the container component */
	borderRadius?: number | string;

	/** indicates if arrows to switch images should be displayed (commonly controlled by hover) */
	displayControls?: boolean;

	/** component to show if image exists but does not load */
	errorComponent?: ReactNode;

	/** image label to display if container node is given */
	imageLabel?: string;
	
	/** determines whether or not the pointer events property should be set to auto */
	isPointerEventsAuto?: boolean;

	/** function to extract image source for given key that overrides default server requests */
	imageSrcFunction?: (node: C8Node, key: string, containerHeight: number, containerWidth: number) => string;

	/** image node (image source will be extracted from it if src not provided) */
	node?: C8Node;

	/** component to show if no image exists */
	noImageComponent?: ReactNode;

	/** callback for image container resize handling. */
	onImageContainerResize?: (calculatedHeight: number, calculatedWidth: number) => void;

	/** callback for image loaded handling */
	onImageLoaded?: (e: React.SyntheticEvent<HTMLImageElement>) => void;

	/** path to image container from given node */
	path?: string;

	/** indicate whether to scale image to container by providing the dimensions of the src image */
	scaleImageWithDimensions?: ImageDimensions;

	/** size of image (if not specified, then image will be rendered to fill available space) */
	size?:
	| "icon"
	| "smallest"
	| "small"
	| "medium"
	| "large"
	| "largest";

	/** dictate size of image by ImageSize enum value such as "ImageSize:Smallest" */
	sizeEnum?: string;

	/** image src */
	src?: string;
}

class ImageStore {
	@observable private _imageKey: string;

	/** height of element containing image */
	@observable calculatedHeight = 0;

	/** width of element containing image */
	@observable calculatedWidth = 0;

	/** indicates if image loaded with an error */
	@observable isError = false;

	/** component properties */
	@observable.ref props: CsiImageProps;

	/** indicates if properties are configured to allow switching between node images */
	@computed get allowSwitchImages(): boolean {
		const { allowSwitchNodeImages, node, path } = this.props;
		return allowSwitchNodeImages && node && !path;
	}

	/** indicates the aspect ratio of the image container */
	get calcuatedAspectRatio(): number {
		return this.calculatedWidth / this.calculatedHeight;
	}

	/** indicates if there are multiple images to display (one at a time with arrows to switch) */
	@computed get hasMultipleImages(): boolean {
		return this.imageKeys.length > 1;
	}

	/** indicates if there is an image to switch to on the right */
	@computed get hasNextKey(): boolean {
		const { imageIndex, imageKeys } = this;
		return imageIndex < (imageKeys.length - 1);
	}

	/** indicates if there is an image to switch to on the left */
	@computed get hasPreviousKey(): boolean {
		const { imageIndex } = this;
		return imageIndex >= 1;
	}

	/** indicates if there is a previous image to display */
	@computed get icon(): boolean {
		return this.resolvedSizeEnum === IMAGE_SIZE_ICON;
	}

	/** index of image to display out of the multiple images to display */
	@computed get imageIndex(): number {
		const { imageKey, imageKeys } = this;
		return imageKeys.indexOf(imageKey);
	}

	/** the key of the image to display from the node */
	@computed get imageKey(): string {
		const { allowSwitchImages, imageKeys, props: { imageLabel, node }, _imageKey } = this;

		if (!allowSwitchImages) {
			return imageLabel;
		}

		if (imageKeys.includes(_imageKey)) {
			return _imageKey;
		}

		return convertImageLabelToNumberedKey(node.Images, imageLabel || DEFAULT_IMAGE_LABEL);
	}

	/** the list of image keys from the node */
	@computed get imageKeys(): string[] {
		const { allowSwitchImages, props: { imageLabel, node } } = this;
		return allowSwitchImages ? getImageContainerNumberedKeys(node.Images) : [imageLabel];
	}

	/** image style is used directly on img ReactNode */
	@computed get imageStyle(): CSSProperties {
		const { props, calcuatedAspectRatio, calculatedHeight, calculatedWidth } = this;
		const { borderRadius, scaleImageWithDimensions } = props;
		const imageStyle: CSSProperties = {
			borderRadius,
		};

		if (!scaleImageWithDimensions) {
			return imageStyle;
		}

		const imageAspectRatio = scaleImageWithDimensions.width / scaleImageWithDimensions.height;
		if (calcuatedAspectRatio < imageAspectRatio) {
			imageStyle.height = "auto";
			imageStyle.width = calculatedWidth;
		} else {
			imageStyle.height = calculatedHeight;
			imageStyle.width = "auto";
		}

		return imageStyle;
	}

	/** indicates if desired size is fixed, rather than growing to the size of the container */
	@computed get isFixedSize(): boolean {
		const { props: { aspectRatio, height, width } } = this;
		return !!(aspectRatio || height || width);
	}

	/** image size enum value that is specified or best matches the desired size of the image */
	@computed get resolvedSizeEnum(): string {
		const { calculatedHeight, calculatedWidth, props, sizeEnum } = this;
		return sizeEnum || getImageSizeByDimensions(calculatedHeight, calculatedWidth, props.allowViewable);
	}

	/** image size property value that is specified or best matches the desired size of the image */
	@computed get resolvedSize(): string {
		const { icon, props, sizeEnum } = this;
		return icon ? "small" : (props.size || ENUM_TO_SIZE_MAP[sizeEnum]);
	}

	/** source of image to display */
	@computed get resolvedSrc(): string {
		const { calculatedHeight, calculatedWidth, icon, imageKey, isError, props, resolvedSizeEnum } = this;
		const { imageSrcFunction, node, onImageContainerResize, path, src } = props;

		if (isError) {
			// if error, then source is not valid
			return null;
		} else if (src) {
			onImageContainerResize?.(calculatedHeight, calculatedWidth);
			// if source given, then return
			return src;
		}

		// call custom function to resolve image source
		if (imageSrcFunction) {
			return imageSrcFunction(node, imageKey, calculatedHeight, calculatedWidth);
		}

		// extract src from node and appropriate attribute for size of image
		const imageId = getImageAttributeBySize(icon ? IMAGE_SIZE_SMALL : resolvedSizeEnum);
		return imageId && getImageSrc(node, path, imageId, imageKey);
	}

	/** configured enum size to display */
	@computed get sizeEnum(): string {
		const { props } = this;
		const { size, sizeEnum } = props;
		return sizeEnum || SIZE_TO_ENUM_MAP[size];
	}

	/** component to use when there is either no image source or an error */
	@computed get unresolvedComponent(): ReactNode {
		const { isError, props } = this;
		const { errorComponent, noImageComponent } = props;
		return isError ? (errorComponent || <BrokenImageIcon />) : noImageComponent;
	}

	/** switch to next image */
	@action nextImageKey = () => {
		const { imageIndex, imageKeys } = this;
		this.isError = false;
		this._imageKey = imageKeys[imageIndex + 1];
	};

	/** switch to previous image */
	@action previousImageKey = () => {
		const { imageIndex, imageKeys } = this;
		this.isError = false;
		this._imageKey = imageKeys[imageIndex - 1];
	};

	/** set dimensions of the container element */
	@action setDimensions = (height: number, width: number) => {
		if (!height || !width) {
			return;
		}
		const { props: { aspectRatio, onImageContainerResize } } = this;
		this.calculatedHeight = aspectRatio ? (width / aspectRatio) : height;
		this.calculatedWidth = width;
		onImageContainerResize?.(this.calculatedHeight, this.calculatedWidth);
	};

	/** indicate there was an error in loading the image */
	@action setError = (isError: boolean) => {
		this.isError = isError;
	};

	/** set the component properties */
	@action setProps = (props: CsiImageProps) => {
		// reset image key when node changes
		if (props.node !== this.props?.node) {
			this._imageKey = null;
		}

		this.props = props;
	};

	constructor(props: CsiImageProps) {
		makeObservable(this);
		this.setProps(props);
	}
}

const CsiImage: FunctionComponent<CsiImageProps> = observer(function CsiImage(props) {
	const theme = useTheme();
	const store = useSyncLocalStore<ImageStore>(() => new ImageStore(props));
	const {
		allowSwitchNodeImages,
		allowViewable,
		aspectRatio,
		borderRadius,
		className,
		displayControls,
		errorComponent,
		height,
		imageLabel,
		imageSrcFunction,
		isPointerEventsAuto,
		node,
		noImageComponent,
		onClick,
		// onImageContainerResize declared here so it won't be included in boxProps below.
		onImageContainerResize,
		onImageLoaded,
		path,
		scaleImageWithDimensions,
		size,
		sizeEnum,
		src,
		...boxProps
	} = props;

	useEffect(() => {
		store.setProps(props);
	}, [
		allowSwitchNodeImages,
		allowViewable,
		aspectRatio,
		borderRadius,
		className,
		displayControls,
		height,
		isPointerEventsAuto,
		imageLabel,
		imageSrcFunction,
		node,
		onClick,
		onImageContainerResize,
		onImageLoaded,
		path,
		scaleImageWithDimensions?.height,
		scaleImageWithDimensions?.width,
		size,
		sizeEnum,
		src,
	]);

	const {
		calculatedHeight,
		hasMultipleImages,
		hasNextKey,
		hasPreviousKey,
		icon,
		isFixedSize,
		imageStyle,
		resolvedSize,
		resolvedSrc,
		nextImageKey,
		previousImageKey,
		setDimensions,
		setError,
	} = store;

	// use to detect height and width of the container to best fit the image
	const { ref } = useResizeObserver<HTMLDivElement>({
		// onResize callback is debounced because when you dynamically resize elements in the DOM, additional resize events are triggered, creating a loop.
		onResize: debounce(({ width, height }) => setDimensions(height, width), 1),
	});

	// CSS class for the image itself based on desired size or up to size of image itself
	const imageClass = clsx(
		classes.image, 
		CSS_MAP[resolvedSize] || classes.max, 
		isPointerEventsAuto && classes.pointerEventsAuto,
	);

	// determine height if using aspect ratio
	const resolvedHeight = (aspectRatio && calculatedHeight) ? calculatedHeight : height;

	return (
		<StyledBox
			borderRadius={borderRadius}
			centerContent
			className={`${isFixedSize ? "" : classes.fullSize} ${className || ""}`}
			height={resolvedHeight}
			{...{ ref }}
			{...boxProps}
		>
			{resolvedSrc && icon && (
				<Tooltip
					slotProps={{
						tooltip: {
							sx: toolTipStyles(theme),
						},
					}}
					title={<img className={imageClass} src={resolvedSrc} />}
				>
					<AddPhotoIcon className={cursorPointer} />
				</Tooltip>
			)}
			{resolvedSrc && !icon && (
				<Fragment>
					<img
						className={`${imageClass} ${onClick ? cursorPointer : ""}`}
						onClick={onClick}
						onError={() => setError(true)}
						onLoad={onImageLoaded}
						src={resolvedSrc}
						style={imageStyle}
					/>
					{displayControls && hasMultipleImages && (
						<Fragment>
							<IconButton
								className={`${classes.arrow} ${classes.arrowLeft}`}
								design="outline"
								disabled={!hasPreviousKey}
								onClick={() => previousImageKey()}
								round
								size="large"
							>
								<ChevronLeftIcon />
							</IconButton>
							<IconButton
								className={`${classes.arrow} ${classes.arrowRight}`}
								design="outline"
								disabled={!hasNextKey}
								onClick={() => nextImageKey()}
								round
								size="large"
							>
								<ChevronRightIcon />
							</IconButton>
						</Fragment>
					)}
				</Fragment>
			)}
			{!resolvedSrc && store.unresolvedComponent}
		</StyledBox>
	);
});

export const EmptyComponent = (
	<FilterHdrIcon
		sx={{
			color: grey[800],
		}}
	/>
);

export interface EmptyCardComponentProps {
	borderless?: boolean;
}

export const EmptyCardComponent: FunctionComponent<EmptyCardComponentProps> = (props) => {
	const { borderless } = props;

	return (
		<Box
			bgcolor={grey[100]}
			border={borderless ? "none" : `1px solid ${grey[200]}`}
			borderRadius={borderless ? "0px" : "3px"}
			centerContent
			height={1}
			width={1}
		>
			<FilterHdrIcon
				sx={{
					color: grey[800],
				}}
			/>
		</Box>
	);
};

export default CsiImage;
